/**
 * useInsuranceTypeLogic Hook
 *
 * Handles insurance type related logic including:
 * - Auto-selection of insurance type based on insurer number
 * - Field state management based on insurance type
 * - Default value setting when insurance type changes
 * - Reimbursement (償還払い) alert logic
 *
 * Matches RA2's culum_control() behavior for insurance-related fields
 */

import { useEffect, useCallback, useRef } from 'react';
import { UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { PatientFormSchemaType } from '@/pages/main/patient/types';
import {
  useDefaultSeikatsuHogoInsurer,
  type DefaultSeikatsuHogoInsurer,
} from '@/pages/main/patient/hooks/useDefaultSeikatsuHogoInsurer';

export interface InsuranceTypeState {
  /** Whether the card mark field should be readonly */
  isCardMarkReadonly: boolean;
  /** Whether the card number field should be readonly */
  isCardNumberReadonly: boolean;
  /** Whether patient cost options should be disabled */
  isPatientCostDisabled: boolean;
  /** Whether elderly insurance certificate options should be disabled */
  isElderlyCertificateDisabled: boolean;
  /** Whether insurer number field should be readonly */
  isInsurerNumberReadonly: boolean;
  /** Label for card number field */
  cardNumberLabel: string;
  /** Whether this is a reimbursement type insurance (償還払い) */
  isReimbursement: boolean;
  /** CSS class for insurer number field (pink for reimbursement) */
  insurerNumberClassName: string;
}

export interface UseInsuranceTypeLogicReturn {
  /** Current state based on insurance type */
  state: InsuranceTypeState;
  /** Handle insurance type change */
  handleInsuranceTypeChange: (newTypeId: number) => void;
  /** Handle insurer number change */
  handleInsurerNumberChange: (value: string) => void;
}

/**
 * Hook for insurance type related logic
 */
export function useInsuranceTypeLogic(
  watch: UseFormWatch<PatientFormSchemaType>,
  setValue: UseFormSetValue<PatientFormSchemaType>
): UseInsuranceTypeLogicReturn {
  const previousInsuranceTypeRef = useRef<number | null>(null);
  const isInitializedRef = useRef(false);

  // Cached fetch of the 生活保護 insurer record so the type-3 branch can fill
  // `insurer_name` / `insurer_kind_name` from real DB values instead of hardcoded
  // strings. Held in a ref so the callback can read the latest value without
  // becoming a dependency.
  const { data: seikatsuHogoInsurer } = useDefaultSeikatsuHogoInsurer();
  const seikatsuHogoInsurerRef = useRef<DefaultSeikatsuHogoInsurer | null | undefined>(
    seikatsuHogoInsurer
  );
  seikatsuHogoInsurerRef.current = seikatsuHogoInsurer;

  const insuranceTypeId = watch('health_insurance_card.insurance_type_id');
  const claimTypeId = watch('health_insurance_card.claim_type_id');

  // Determine insurance type states
  const isKoukiKourei = insuranceTypeId === 2; // 後期高齢
  const isSeikatsuHogo = insuranceTypeId === 3; // 生活保護
  const isReimbursement = claimTypeId === 3; // 償還払い

  // Compute state
  const state: InsuranceTypeState = {
    isCardMarkReadonly: isKoukiKourei || isSeikatsuHogo,
    isCardNumberReadonly: isSeikatsuHogo,
    isPatientCostDisabled: isKoukiKourei,
    isElderlyCertificateDisabled: isKoukiKourei,
    isInsurerNumberReadonly: isSeikatsuHogo,
    cardNumberLabel: isKoukiKourei ? '被保険者番号' : '番号',
    isReimbursement,
    insurerNumberClassName: isReimbursement ? 'border-pink-500 bg-pink-50' : '',
  };

  // ==================== Insurance Type Change Handler ====================

  const handleInsuranceTypeChange = useCallback(
    (newTypeId: number) => {
      const previousTypeId = previousInsuranceTypeRef.current;
      previousInsuranceTypeRef.current = newTypeId;

      // Don't apply defaults on initial load
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        return;
      }

      // Don't apply defaults if switching to same type
      if (previousTypeId === newTypeId) {
        return;
      }

      // Clear claim type and reimbursement styling
      setValue('health_insurance_card.claim_type_id', null);

      // Apply type-specific defaults
      switch (newTypeId) {
        case 2: // 後期高齢
          // Clear card mark
          setValue('health_insurance_card.card_mark', '');
          // Clear patient cost selections
          setValue('health_insurance_card.patient_cost_id', undefined);
          // Set elderly certificate to "なし" (id: 1)
          setValue('health_insurance_card.elderly_insurance_certificate_id', 1);
          // No893: 後期高齢は本人のみ — enforced on submit (useCrossSectionValidation); do not lock 続柄 in UI
          // Set kind name
          setValue('health_insurance_card.insurer_kind_name', '後期高齢');
          // Clear other fields if this is a user-initiated change
          setValue('health_insurance_card.insurer_number', '');
          setValue('health_insurance_card.insurer_name', '');
          setValue('health_insurance_card.patient_cost_rate', '');
          setValue('health_insurance_card.card_number', '');
          break;

        case 3: // 生活保護
          // Set default insurer number — `00000001` is a real row in the
          // `insurers` table, not a hardcoded constant. The corresponding
          // `insurer_name` / `insurer_kind_name` are read from that row below.
          setValue('health_insurance_card.insurer_number', '00000001');
          // Set default card mark and number
          setValue('health_insurance_card.card_mark', '１');
          setValue('health_insurance_card.card_number', '１');
          // Clear patient cost
          setValue('health_insurance_card.patient_cost_id', undefined);
          setValue('health_insurance_card.elderly_insurance_certificate_id', undefined);
          setValue('health_insurance_card.patient_cost_rate', '');
          // Pull insurer_name / insurer_kind_name from the cached DB record. If
          // the lookup hasn't resolved yet, leave those two fields untouched
          // rather than writing stale hardcoded strings.
          {
            const seikatsuHogo = seikatsuHogoInsurerRef.current;
            if (seikatsuHogo) {
              setValue('health_insurance_card.insurer_id', seikatsuHogo.id);
              setValue('health_insurance_card.insurer_name', seikatsuHogo.insurer_name);
              setValue(
                'health_insurance_card.insurer_kind_name',
                seikatsuHogo.insurer_kind_name
              );
            }
          }
          break;

        case 1: // 一般
        case 4: // 自由診療
        default:
          // Clear fields (but not card_mark since it's required for types 1 and 4)
          setValue('health_insurance_card.insurer_number', '');
          setValue('health_insurance_card.insurer_name', '');
          setValue('health_insurance_card.insurer_kind_name', '');
          // Don't clear card_mark for types 1 and 4 as it's required
          // setValue("health_insurance_card.card_mark", "");
          setValue('health_insurance_card.card_number', '');
          setValue('health_insurance_card.patient_cost_rate', '');
          // Set patient cost to "なし" (id: 0)
          setValue('health_insurance_card.patient_cost_id', 0);
          break;
      }
    },
    [setValue]
  );

  // ==================== Insurer Number Change Handler ====================

  const handleInsurerNumberChange = useCallback(
    (value: string) => {
      if (!value) return;

      // Normalize to half-width and pad to 8 digits
      const normalized = value
        .replace(/[０-９]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
        .padStart(8, '0');

      setValue('health_insurance_card.insurer_number', normalized);

      // Auto-select insurance type based on number (RA2: No504)
      if (normalized.startsWith('39')) {
        // 後期高齢の保険者番号
        setValue('health_insurance_card.insurance_type_id', 2);
        setValue('health_insurance_card.insurer_kind_name', '後期高齢');
      } else if (normalized === '00000001') {
        // 生活保護
        setValue('health_insurance_card.insurance_type_id', 3);
        setValue('health_insurance_card.insurer_kind_name', '生活保護');
      } else if (
        !normalized.startsWith('39') &&
        normalized !== '00000001' &&
        insuranceTypeId !== 1
      ) {
        // Not 後期高齢 or 生活保護, set to 一般
        setValue('health_insurance_card.insurance_type_id', 1);
        setValue('health_insurance_card.insurer_kind_name', '');
      }
    },
    [setValue, insuranceTypeId]
  );

  // ==================== Effect: Update state when insurance type changes ====================

  useEffect(() => {
    if (insuranceTypeId) {
      previousInsuranceTypeRef.current = insuranceTypeId;
    }
  }, [insuranceTypeId]);

  return {
    state,
    handleInsuranceTypeChange,
    handleInsurerNumberChange,
  };
}

/**
 * Hook for medical aid type logic
 */
export function useMedicalAidTypeLogic(
  watch: UseFormWatch<PatientFormSchemaType>,
  setValue: UseFormSetValue<PatientFormSchemaType>
): {
  isFieldsDisabled: boolean;
  handleMedicalAidTypeChange: (newTypeId: number) => void;
} {
  const insurerTypeId = watch('medical_aid.insurer_type_id');
  const isFieldsDisabled = insurerTypeId === 0; // なし

  const handleMedicalAidTypeChange = useCallback(
    (newTypeId: number) => {
      setValue('medical_aid.insurer_type_id', newTypeId);

      // Reset fields when type changes (RA2: No946)
      if (newTypeId === 0) {
        // なし
        setValue('medical_aid.medical_aid_number', '');
        setValue('medical_aid.medical_aid_patient_number', '');
        setValue('medical_aid.insurer_cost_rate', '');
        setValue('medical_aid.is_partial_aid', false);
        setValue('medical_aid.is_special', false);
        setValue('medical_aid.calc_type', null);
        setValue('medical_aid.limit_amount', null);
        setValue('medical_aid.limit_num_month', null);
        setValue('medical_aid.limit_amount_month', null);
      } else {
        // Clear fields for new selection
        setValue('medical_aid.medical_aid_number', '');
        setValue('medical_aid.medical_aid_patient_number', '');
        setValue('medical_aid.insurer_cost_rate', '');
        setValue('medical_aid.is_partial_aid', false);
        setValue('medical_aid.is_special', false);
      }
    },
    [setValue]
  );

  return {
    isFieldsDisabled,
    handleMedicalAidTypeChange,
  };
}

export default useInsuranceTypeLogic;
